import { useEffect, useState, useMemo, useRef } from 'react'
import { Loader2, ArrowRight, LayoutGrid, LayoutDashboard, LayoutList } from 'lucide-react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { JourneyProgressCard } from '../dashboard/JourneyProgressCard'
import { DomainSummaryCards } from '../dashboard/DomainSummaryCards'
import { GuidanceStrip } from '../dashboard/GuidanceStrip'
import { OverviewMode } from './OverviewMode'
import { CompactMode } from './CompactMode'
import type { WorkspaceMode } from '@/stores/workspaceStore'

const DASH_VIEW_KEY = 'skerpa_dash_view'
type DashView = 'classic' | 'overview' | 'compact'

function readDashView(): DashView {
  try {
    const stored = localStorage.getItem(DASH_VIEW_KEY)
    if (stored === 'classic') return 'classic'
    if (stored === 'overview') return 'overview'
    return 'compact'
  } catch {
    return 'classic'
  }
}

interface DashboardModeProps {
  context: {
    mode: WorkspaceMode
    phaseId: string
    journeyMode: 'structured' | 'free'
  }
  onViewChange?: (view: string) => void
}

export function DashboardMode({ onViewChange }: DashboardModeProps) {
  const { accountId, projectId } = useSessionStore()
  const { setShowGoalWizard } = useWorkspaceStore()
  const {
    isLoading,
    fetchJourney,
    getSelectedGoal,
    getCurrentPhase,
    isActivityCompleted,
  } = useJourneyStore()

  const [dashView, setDashView] = useState<DashView>(readDashView)
  const [expandedPhaseId, setExpandedPhaseId] = useState<string | null>(null)
  const stepperRef = useRef<HTMLDivElement | null>(null)

  const handleToggleView = (view: DashView) => {
    setDashView(view)
    try { localStorage.setItem(DASH_VIEW_KEY, view) } catch { /* ignore */ }
  }

  useEffect(() => {
    if (accountId && projectId) {
      void fetchJourney(accountId, projectId)
    }
  }, [accountId, projectId, fetchJourney])

  const goal = getSelectedGoal()
  const currentPhase = getCurrentPhase()

  const nextActivity = useMemo(() => {
    if (!currentPhase) return null
    return currentPhase.activities.find(a => !isActivityCompleted(a.id)) ?? null
  }, [currentPhase, isActivityCompleted])

  const handleContinue = () => {
    if (!goal) {
      setShowGoalWizard(true)
      return
    }
    if (currentPhase) {
      setExpandedPhaseId(currentPhase.id)
      // give the stepper a tick to render the expanded panel before scrolling
      requestAnimationFrame(() => {
        stepperRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }

  const handleTogglePhase = (phaseId: string) => {
    setExpandedPhaseId(prev => (prev === phaseId ? null : phaseId))
  }

  // View toggle — rendered in both loading and loaded states
  const viewToggle = (
    <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-warm-100 border border-border">
      <button
        type="button"
        onClick={() => handleToggleView('classic')}
        title="Classic view"
        className={[
          'p-1.5 rounded-md transition-colors',
          dashView === 'classic'
            ? 'bg-bg-card text-text shadow-sm'
            : 'text-text-muted hover:text-text',
        ].join(' ')}
      >
        <LayoutGrid className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => handleToggleView('overview')}
        title="Overview"
        className={[
          'p-1.5 rounded-md transition-colors',
          dashView === 'overview'
            ? 'bg-bg-card text-text shadow-sm'
            : 'text-text-muted hover:text-text',
        ].join(' ')}
      >
        <LayoutDashboard className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => handleToggleView('compact')}
        title="Compact view"
        className={[
          'p-1.5 rounded-md transition-colors',
          dashView === 'compact'
            ? 'bg-bg-card text-text shadow-sm'
            : 'text-text-muted hover:text-text',
        ].join(' ')}
      >
        <LayoutList className="w-3.5 h-3.5" />
      </button>
    </div>
  )

  // Overview and compact render immediately — they manage their own loading skeletons
  if (dashView === 'overview') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-end p-4 pb-0 flex-shrink-0">{viewToggle}</div>
        <OverviewMode onViewChange={onViewChange} />
      </div>
    )
  }

  if (dashView === 'compact') {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-end p-4 pb-0 flex-shrink-0">{viewToggle}</div>
        <CompactMode onViewChange={onViewChange} />
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex justify-end p-4">{viewToggle}</div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 size={28} className="animate-spin text-text-muted mx-auto mb-3" />
            <p className="text-sm text-text-muted">Loading dashboard…</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto py-6">
      <div className="w-full">
        <div className="flex items-start justify-between gap-4">
          <ContinueCta
            hasGoal={!!goal}
            phaseName={currentPhase?.name ?? null}
            activityName={nextActivity?.name ?? null}
            phaseColor={currentPhase?.color}
            onContinue={handleContinue}
          />
          <div className="flex-shrink-0 mt-0.5">{viewToggle}</div>
        </div>

        <div ref={stepperRef} className="mt-8">
          <JourneyProgressCard
            expandedPhaseId={expandedPhaseId}
            onTogglePhase={handleTogglePhase}
          />
        </div>

        <div className="mt-9">
          <DomainSummaryCards onViewChange={onViewChange} />
        </div>

        <div className="mt-6">
          <GuidanceStrip onViewChange={onViewChange} />
        </div>
      </div>
    </div>
  )
}

interface ContinueCtaProps {
  hasGoal: boolean
  phaseName: string | null
  activityName: string | null
  phaseColor?: string
  onContinue: () => void
}

function ContinueCta({ hasGoal, phaseName, activityName, phaseColor, onContinue }: ContinueCtaProps) {
  if (!hasGoal) {
    return (
      <section aria-label="Get started" className="flex items-end justify-between gap-4 pb-1">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
            Get started
          </div>
          <h2 className="font-display text-[22px] font-medium leading-tight text-text tracking-[-0.012em]">
            Pick a journey to set the path.
          </h2>
        </div>
        <button
          onClick={onContinue}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--color-accent)] hover:bg-[var(--color-accent-700)] rounded-lg transition-colors"
        >
          Choose journey
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </section>
    )
  }

  if (!phaseName) {
    return (
      <section aria-label="Journey complete" className="pb-1">
        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
          Journey complete
        </div>
        <h2 className="font-display text-[22px] font-medium leading-tight text-text tracking-[-0.012em]">
          You&rsquo;ve worked through every phase.
        </h2>
      </section>
    )
  }

  if (!activityName) {
    return (
      <section aria-label="Phase ready to advance" className="flex items-end justify-between gap-4 pb-1">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
            {phaseColor && (
              <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phaseColor }} />
            )}
            <span>{phaseName} ready to advance</span>
          </div>
          <h2 className="font-display text-[22px] font-medium leading-tight text-text tracking-[-0.012em]">
            Review the phase and move on.
          </h2>
        </div>
        <button
          onClick={onContinue}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-text border border-warm-300 hover:border-warm-400 rounded-lg transition-colors bg-white"
        >
          Open phase
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </section>
    )
  }

  return (
    <section aria-label="Continue your journey" className="flex items-end justify-between gap-4 pb-1">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-2">
          {phaseColor && (
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: phaseColor }} />
          )}
          <span>Up next in {phaseName}</span>
        </div>
        <h2 className="font-display text-[22px] font-medium leading-tight text-text tracking-[-0.012em] truncate">
          {activityName}
        </h2>
      </div>
      <button
        onClick={onContinue}
        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-[var(--color-accent)] hover:bg-[var(--color-accent-700)] rounded-lg transition-colors"
      >
        Continue
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </section>
  )
}
